/**
 * The first-run journey's pure core: what the user is asked, how their
 * answers reshape the scene around them, and what gets saved.
 *
 * Kept free of React and of the DOM so the interesting parts — the scene
 * derivation and the two payload shapes — can be tested directly. The
 * component does nothing but draw what these functions return.
 */

// ── What we ask ───────────────────────────────────────────────────────────

export type BeatId =
  | 'arrival' | 'about' | 'people' | 'interests' | 'formats'
  | 'books' | 'shows' | 'screens' | 'practice'
  | 'rhythm' | 'direction' | 'offclock' | 'reveal';

/**
 * The order, told as one story: who we are talking to, who is around them,
 * what pulls them, how they take it in, how fast, where they are going, who
 * they are off the clock — and then what all of that gives them.
 */
export const BEATS: BeatId[] = [
  'arrival', 'about', 'people', 'interests', 'formats',
  // One follow-up per way of learning, asked only of the people who chose it.
  'books', 'shows', 'screens', 'practice',
  'rhythm', 'direction', 'offclock', 'reveal',
];

/** Which format each follow-up belongs to. */
const BEAT_FORMAT: Partial<Record<BeatId, string>> = {
  books: 'read', shows: 'listen', screens: 'watch', practice: 'do',
};

/**
 * The formats worth a follow-up.
 *
 * Capped at the same two the room draws. Somebody who ticks all four is
 * telling us they are easy to please, not asking for four more pages — and
 * thirteen beats is already the most this should ever be.
 */
export function detailFormats(a: JourneyAnswers): string[] {
  return a.formats.slice(0, MAX_MOTIFS);
}

/**
 * Beats that are only asked when an earlier answer makes them worth asking.
 * Nobody who never reads should be handed a list of book titles.
 */
export function beatApplies(beat: BeatId, a: JourneyAnswers): boolean {
  const format = BEAT_FORMAT[beat];
  if (format) return detailFormats(a).includes(format);
  return true;
}

/** The beats this particular person will actually walk through. */
export function visibleBeats(a: JourneyAnswers): BeatId[] {
  return BEATS.filter((b) => beatApplies(b, a));
}

/**
 * Progress is a position in the list this person is actually walking, not a
 * fixed number per beat — otherwise skipping the books beat makes the bar
 * jump, which reads as though something was missed.
 */
export function progressFor(beat: BeatId, a: JourneyAnswers): number {
  const list = visibleBeats(a);
  const at = list.indexOf(beat);
  if (at < 0) return 0;
  return Math.round(((at + 1) / list.length) * 100);
}

export interface Choice {
  value: string;
  label: string;
  /** One line of why this matters, shown under the choice once picked. */
  hint?: string;
}

/**
 * Interests drive the palette. These are the same twelve the original
 * questionnaire used, so existing StartUpQuestions rows and anything reading
 * `interestTopics` keep their vocabulary.
 */
export const INTERESTS: Choice[] = [
  { value: 'Leadership', label: 'Leadership' },
  { value: 'Human Behaviour', label: 'Human behaviour' },
  { value: 'Communication', label: 'Communication' },
  { value: 'Inspiration', label: 'Inspiration' },
  { value: 'Self-Care', label: 'Self-care' },
  { value: 'Mental Wellbeing', label: 'Mental wellbeing' },
  { value: 'Physical Wellbeing', label: 'Physical wellbeing' },
  { value: 'Mindfulness', label: 'Mindfulness' },
  { value: 'Finance', label: 'Finance' },
  { value: 'Technology', label: 'Technology' },
  { value: 'Innovation', label: 'Innovation' },
  { value: 'Nutrition', label: 'Nutrition' },
];

/**
 * How someone takes something in. This is the answer the scene reacts to
 * most visibly — pick reading and the room fills with paper, pick listening
 * and it becomes sound.
 */
export const FORMATS: Choice[] = [
  { value: 'read', label: 'Reading', hint: 'Articles, book chapters, summaries you can scan' },
  { value: 'listen', label: 'Listening', hint: 'Podcasts and talks for the commute or the gym' },
  { value: 'watch', label: 'Watching', hint: 'Short video, talks, someone showing you' },
  { value: 'do', label: 'Doing', hint: 'Exercises and practice, learning with your hands' },
];

export const TIME_OPTIONS: Choice[] = [
  { value: '5 minutes', label: 'About 5 minutes', hint: 'One idea at a time, between things' },
  { value: '15 minutes', label: 'Around 15', hint: 'Long enough to finish a thought' },
  { value: '30 minutes', label: 'Half an hour', hint: 'Room to go properly into something' },
  { value: '60 minutes', label: 'An hour or more', hint: 'You would rather go deep than often' },
];

export const EXPERIENCE_OPTIONS: Choice[] = [
  { value: 'beginner', label: 'Starting out', hint: 'Begin at the beginning, skip nothing' },
  { value: 'intermediate', label: 'Finding my feet', hint: 'Past the basics, still building' },
  { value: 'advanced', label: 'Well into it', hint: 'Go straight to the substance' },
  { value: 'expert', label: 'I teach this', hint: 'Only the edges are interesting now' },
];

export const GOAL_OPTIONS: Choice[] = [
  { value: 'Step into a leadership role', label: 'Step into leadership' },
  { value: 'Get better at my current craft', label: 'Get better at my craft' },
  { value: 'Move into a different field', label: 'Move into a new field' },
  { value: 'Lead a bigger team', label: 'Lead a bigger team' },
  { value: 'Build commercial judgement', label: 'Build commercial judgement' },
  { value: 'Speak and present with confidence', label: 'Speak with confidence' },
  { value: 'Work better with difficult people', label: 'Handle hard conversations' },
  { value: 'Find more balance', label: 'Find more balance' },
];

export const GAP_OPTIONS: Choice[] = [
  { value: 'Giving feedback', label: 'Giving feedback' },
  { value: 'Difficult conversations', label: 'Difficult conversations' },
  { value: 'Time management', label: 'Managing my time' },
  { value: 'Presenting', label: 'Presenting' },
  { value: 'Delegating', label: 'Delegating' },
  { value: 'Strategic thinking', label: 'Thinking strategically' },
  { value: 'Data and numbers', label: 'Data and numbers' },
  { value: 'Staying focused', label: 'Staying focused' },
  { value: 'Managing stress', label: 'Managing stress' },
  { value: 'Writing clearly', label: 'Writing clearly' },
];

export const HOBBY_OPTIONS: Choice[] = [
  { value: 'Reading', label: 'Reading' }, { value: 'Cooking', label: 'Cooking' },
  { value: 'Baking', label: 'Baking' }, { value: 'Outdoor Activities', label: 'The outdoors' },
  { value: 'Tech/Computers', label: 'Tech' }, { value: 'Traveling', label: 'Travel' },
  { value: 'Socialising', label: 'People' }, { value: 'Gaming', label: 'Gaming' },
  { value: 'Gardening', label: 'Gardening' }, { value: 'DIY', label: 'Making things' },
  { value: 'Arts', label: 'Art' }, { value: 'Writing', label: 'Writing' },
  { value: 'Photography', label: 'Photography' }, { value: 'Sport', label: 'Sport' },
  { value: 'Music', label: 'Music' },
];

export const RECHARGE_OPTIONS: Choice[] = [
  { value: 'Reading', label: 'Reading' }, { value: 'Exercise', label: 'Moving' },
  { value: 'Listen to Music', label: 'Music' }, { value: 'Hiking', label: 'Being outside' },
  { value: 'Meditate', label: 'Sitting quietly' }, { value: 'Sleeping', label: 'Sleep' },
  { value: 'Yoga', label: 'Yoga' }, { value: 'Cycling', label: 'Cycling' },
  { value: 'Running', label: 'Running' }, { value: 'Spa Treatment', label: 'Being looked after' },
];

export const GENDER_OPTIONS: Choice[] = [
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' },
  { value: 'Prefer not to say', label: 'Rather not say' },
];

export const AGE_OPTIONS: Choice[] = ['20-25', '26-35', '36-45', '46-55', '55+']
  .map((v) => ({ value: v, label: v }));

export const RELATIONSHIP_OPTIONS: Choice[] = ['Single', 'Partnered', 'Engaged', 'Married']
  .map((v) => ({ value: v, label: v }));

/** The old form's five, which reporting already groups by. */
export const PERSONALITY_OPTIONS: Choice[] = [
  { value: 'Social Butterfly', label: 'Social butterfly', hint: 'Happiest with people around' },
  { value: 'Extrovert', label: 'Extrovert', hint: 'Out loud, out there' },
  { value: 'Introvert', label: 'Introvert', hint: 'Recharges on your own' },
  { value: 'Family Person', label: 'Family person', hint: 'Home is the centre of it' },
  { value: 'Workaholic', label: 'Workaholic', hint: 'Hard to put it down' },
];

export const INSPIRATION_OPTIONS: Choice[] = [
  { value: 'Simon Sinek (Thought Leader)', label: 'Simon Sinek' },
  { value: 'Nelson Mandela (Peace Activist)', label: 'Nelson Mandela' },
  { value: 'Oprah Winfrey (Media Leader)', label: 'Oprah Winfrey' },
  { value: 'Brene Brown (Researcher)', label: 'Brené Brown' },
  { value: 'Michelle Obama (Advocate)', label: 'Michelle Obama' },
  { value: 'Steve Jobs (Visionary)', label: 'Steve Jobs' },
  { value: 'Elon Musk (Innovator)', label: 'Elon Musk' },
  { value: 'Tony Robbins (Motivational Speaker)', label: 'Tony Robbins' },
  { value: 'Richard Branson (Entrepreneur)', label: 'Richard Branson' },
  { value: 'Malala Yousafzai (Activist)', label: 'Malala Yousafzai' },
];

export const BOOKS_OR_SUMMARIES_OPTIONS: Choice[] = [
  { value: 'Books', label: 'The whole book', hint: 'You want the long version' },
  { value: 'Summaries', label: 'A good summary', hint: 'The idea, without the 300 pages' },
  { value: 'Both', label: 'Either', hint: 'Depends on the book' },
];

export const LISTENING_MOMENTS: Choice[] = [
  { value: 'Commuting', label: 'On the way to work' },
  { value: 'Walking', label: 'Walking' },
  { value: 'Training', label: 'At the gym' },
  { value: 'Chores', label: 'Doing chores' },
  { value: 'Cooking', label: 'Cooking' },
  { value: 'Winding down', label: 'Winding down' },
];

export const LISTENING_STYLES: Choice[] = [
  { value: 'Interview', label: 'Two people talking', hint: 'A host and a guest, going somewhere' },
  { value: 'Solo', label: 'One voice', hint: 'Somebody who knows the subject, explaining it' },
  { value: 'Narrative', label: 'A story', hint: 'Reported, produced, with a shape to it' },
  { value: 'Debate', label: 'An argument', hint: 'People who disagree, in good faith' },
];

export const WATCHING_STYLES: Choice[] = [
  { value: 'Talk', label: 'A talk', hint: 'One person on a stage with something to say' },
  { value: 'Demonstration', label: 'Being shown', hint: 'Watch it done, then do it' },
  { value: 'Documentary', label: 'A documentary', hint: 'Longer, made properly' },
  { value: 'Short', label: 'Something short', hint: 'Under ten minutes, to the point' },
];

export const PRACTICE_AREAS: Choice[] = [
  { value: 'Difficult conversations', label: 'Difficult conversations' },
  { value: 'Presenting', label: 'Presenting' },
  { value: 'Negotiating', label: 'Negotiating' },
  { value: 'Interviewing', label: 'Interviewing people' },
  { value: 'Giving feedback', label: 'Giving feedback' },
  { value: 'Writing', label: 'Writing' },
  { value: 'Planning', label: 'Planning work' },
  { value: 'Coaching', label: 'Coaching someone' },
];

export const PRACTICE_COMPANY: Choice[] = [
  { value: 'Alone', label: 'On my own', hint: 'Let me try it before anyone watches' },
  { value: 'With a partner', label: 'With one other person' },
  { value: 'In a group', label: 'In a group', hint: 'I do not mind an audience' },
];

export const TOP_SHOWS = [
  'The Tim Ferriss Show', 'Huberman Lab', 'How I Built This', 'WorkLife with Adam Grant',
  'Hidden Brain', 'The Diary of a CEO', 'Freakonomics Radio', 'A Bit of Optimism',
  'Coaching for Leaders', 'Unlocking Us',
];

export const TOP_CHANNELS = [
  'TED', 'Veritasium', 'Kurzgesagt', 'Ali Abdaal', 'Harvard Business Review',
  'Big Think', 'Google TechTalks', 'The School of Life',
];

export const FAMILY_RELATIONSHIPS = [
  'Spouse', 'Partner', 'Parent', 'Sibling', 'Grandparent', 'Friend',
];

/** Children are family members too — the beat just asks for them separately. */
export const CHILD_RELATIONSHIP = 'Child';

export interface FamilyMemberInput {
  name: string;
  relationship: string;
  birthdate: string;
}

// ── What the user has told us so far ──────────────────────────────────────

export interface JourneyAnswers {
  // Who we are talking to
  gender: string;
  ageRange: string;
  dateOfBirth: string;
  personalityType: string;
  // Who is around them
  relationshipStatus: string;
  hasChildren: boolean;
  /**
   * Kept as full rows rather than a list of dates: a birthday message that
   * cannot say the child's name is not worth sending.
   */
  children: FamilyMemberInput[];
  familyMembers: FamilyMemberInput[];
  // What they want
  interests: string[];
  formats: string[];
  favouriteBooks: string[];
  favouriteAuthors: string[];
  booksOrSummaries: string;
  /** Listening */
  favouriteShows: string[];
  listeningMoments: string[];
  listeningStyle: string;
  /** Watching */
  favouriteChannels: string[];
  watchingStyle: string;
  /** Doing */
  practiceAreas: string[];
  practiceCompany: string;
  timeAvailability: string;
  experienceLevel: string;
  careerGoals: string[];
  skillGaps: string[];
  lifePrincipleInspirations: string[];
  // Off the clock
  hobbies: string[];
  recharge: string[];
}

export const EMPTY_ANSWERS: JourneyAnswers = {
  gender: '', ageRange: '', dateOfBirth: '', personalityType: '',
  relationshipStatus: '', hasChildren: false, children: [], familyMembers: [],
  interests: [], formats: [], favouriteBooks: [], favouriteAuthors: [], booksOrSummaries: '',
  favouriteShows: [], listeningMoments: [], listeningStyle: '',
  favouriteChannels: [], watchingStyle: '',
  practiceAreas: [], practiceCompany: '',
  timeAvailability: '', experienceLevel: '',
  careerGoals: [], skillGaps: [], lifePrincipleInspirations: [],
  hobbies: [], recharge: [],
};

/** Which beats cannot be left until something has been said. */
export function canAdvance(beat: BeatId, a: JourneyAnswers): boolean {
  switch (beat) {
    case 'about': return !!a.gender && !!a.personalityType;
    case 'interests': return a.interests.length > 0;
    case 'formats': return a.formats.length > 0;
    case 'rhythm': return !!a.timeAvailability && !!a.experienceLevel;
    // Everything else — family, books, goals, hobbies — is offered, never
    // demanded. Somebody who would rather not say should still get through.
    default: return true;
  }
}

/** Why the Continue button is not available yet, in the user's words. */
export function blockedReason(beat: BeatId, a: JourneyAnswers): string | null {
  if (canAdvance(beat, a)) return null;
  switch (beat) {
    case 'about': return !a.gender ? 'Pick one, or "rather not say"' : 'And which of these sounds most like you?';
    case 'interests': return 'Pick at least one — you can change these later';
    case 'formats': return 'Pick at least one way you like to take things in';
    case 'rhythm': return !a.timeAvailability ? 'How long is a usual sitting?' : 'And roughly where are you?';
    default: return null;
  }
}

// ── The scene the answers build ───────────────────────────────────────────

/**
 * The motif is the texture of the room. It comes from how the person takes
 * things in, which is the thing they can see changing as they answer: choose
 * reading and pages drift past, choose listening and the room becomes rings
 * of sound.
 */
export type Motif = 'paper' | 'sound' | 'light' | 'grid';

/** How many textures the room will draw at once, however many were picked. */
export const MAX_MOTIFS = 2;

export const MOTIF_BY_FORMAT: Record<string, Motif> = {
  read: 'paper', listen: 'sound', watch: 'light', do: 'grid',
};

export const MOTIF_LABEL: Record<Motif, string> = {
  paper: 'Pages and quiet',
  sound: 'Something in your ears',
  light: 'Watch it happen',
  grid: 'Hands on it',
};

/**
 * Interest → the colour it brings into the room.
 *
 * Saturated rather than pastel on purpose: at the opacities the aurora runs,
 * tints wash out into one pale cloud and every answer looks the same. These
 * are the 500/600 steps, so picking Leadership reads as amber across the whole
 * screen and picking Finance reads as blue.
 */
const INTEREST_COLOR: Record<string, string> = {
  Leadership: '#F97316',
  'Human Behaviour': '#8B5CF6',
  Communication: '#EC4899',
  Inspiration: '#F59E0B',
  'Self-Care': '#10B981',
  'Mental Wellbeing': '#14B8A6',
  'Physical Wellbeing': '#22C55E',
  Mindfulness: '#06B6D4',
  Finance: '#0EA5E9',
  Technology: '#3B82F6',
  Innovation: '#6366F1',
  Nutrition: '#84CC16',
};

/** Where the room starts before anyone has said anything. */
export const BASE_PALETTE = ['#3B82F6', '#8B5CF6', '#06B6D4'];

/**
 * How a colour should arrive.
 *
 * The change is an event, and the kind of event depends on what was asked
 * for. Once somebody has said how they take things in, their texture decides
 * it — a reader's colour turns in like a page, a listener's ripples. Before
 * that, on the beat where the interests are actually being picked, the
 * interest itself decides: leadership bursts, mindfulness comes in like a
 * tide, finance wipes across, nutrition rises.
 */
export type PaletteTransition = 'burst' | 'tide' | 'wipe' | 'rise';

const INTEREST_TEMPERAMENT: Record<string, PaletteTransition> = {
  Leadership: 'burst',
  Inspiration: 'burst',
  Innovation: 'burst',
  Communication: 'burst',
  Mindfulness: 'tide',
  'Self-Care': 'tide',
  'Mental Wellbeing': 'tide',
  'Human Behaviour': 'tide',
  Finance: 'wipe',
  Technology: 'wipe',
  Nutrition: 'rise',
  'Physical Wellbeing': 'rise',
};

const TRANSITION_BY_MOTIF: Record<Motif, PaletteTransition> = {
  paper: 'wipe',
  sound: 'tide',
  light: 'burst',
  grid: 'rise',
};

export function paletteTransition(a: JourneyAnswers): PaletteTransition {
  const motif = a.formats.length ? MOTIF_BY_FORMAT[a.formats[0]] : null;
  if (motif) return TRANSITION_BY_MOTIF[motif];
  // The newest interest leads: it is the one that just changed the room.
  const last = a.interests[a.interests.length - 1];
  return INTEREST_TEMPERAMENT[last] || 'burst';
}

/** Shared so the daily check-in can rebuild the same room from saved answers. */
export function paletteFromInterests(interests: string[]): string[] {
  const chosen = (interests || []).map((i) => INTEREST_COLOR[i]).filter(Boolean);
  if (chosen.length >= 3) return chosen.slice(0, 5);
  return [...chosen, ...BASE_PALETTE.filter((c) => !chosen.includes(c))].slice(0, 3);
}

const TEMPO_BY_TIME: Record<string, number> = {
  '5 minutes': 1.45, '15 minutes': 1.15, '30 minutes': 0.9, '60 minutes': 0.7,
};

export interface Scene {
  /** Aurora blob colours, in the order they were chosen. */
  palette: string[];
  /** The leading texture — what the room is mostly made of. */
  motif: Motif;
  /** Every texture in play, in the order the formats were picked. */
  motifs: Motif[];
  /** Multiplier on drift speed: >1 is quicker, <1 slower and wider. */
  tempo: number;
  /** The strongest colour, used for accents and the progress hairline. */
  accent: string;
  /** One short line naming the room, shown quietly as it changes. */
  label: string;
  /** How a new colour should arrive. */
  transition: PaletteTransition;
}

/**
 * Build the room from what has been said so far.
 *
 * Every answer moves it a little, and the scene is derived rather than
 * stored, so going back and changing an answer takes the room back with it.
 */
export function sceneFor(a: JourneyAnswers): Scene {
  const chosen = a.interests.map((i) => INTEREST_COLOR[i]).filter(Boolean);
  // Always keep at least three blobs in the sky: too few and the aurora
  // reads as a flat wash rather than weather.
  const palette = paletteFromInterests(a.interests);

  // Two textures at once is a combination; three or four is a mess. The
  // formats themselves are all kept — they decide what content gets sent —
  // but only the first two picked are ever drawn.
  const motifs = a.formats.map((f) => MOTIF_BY_FORMAT[f]).filter(Boolean).slice(0, MAX_MOTIFS);
  const motif = motifs[0] || 'light';
  const tempo = TEMPO_BY_TIME[a.timeAvailability] ?? 1;
  const accent = chosen[0] || BASE_PALETTE[0];

  const interestLabel = a.interests.length
    ? a.interests.length === 1 ? a.interests[0] : `${a.interests[0]} and ${a.interests.length - 1} more`
    : '';
  // Naming every texture would run off the edge; the lead plus a count reads
  // the same and fits.
  const motifLabel = !a.formats.length ? ''
    : motifs.length > 1 ? `${MOTIF_LABEL[motif]} +${motifs.length - 1}`
    : MOTIF_LABEL[motif];
  const label = [interestLabel, motifLabel].filter(Boolean).join(' · ');

  return {
    palette, motif, motifs: motifs.length ? motifs : [], tempo, accent, label,
    transition: paletteTransition(a),
  };
}

// ── What gets saved ───────────────────────────────────────────────────────

/**
 * The learning profile is the one the microlearning generator reads first —
 * it beats the questionnaire wherever both are set. Nothing used to write it
 * except a profile editor almost nobody opens, so the strongest signals in
 * the prompt were always blank. The journey fills it directly.
 */
export interface LearningProfilePayload {
  learningStyle: string;
  preferredContentTypes: string[];
  interests: string[];
  skillGaps: string[];
  careerGoals: string[];
  experienceLevel: string;
  timeAvailability: string;
  /**
   * What somebody said about the formats they chose — the shows already in
   * their ears, the channels they watch, what they would rather practise than
   * read about. "Podcast" tells the generator a shape; "The Tim Ferriss Show,
   * listened to while walking, two people talking" tells it what to look for.
   */
  formatPreferences: FormatPreferences;
}

export interface FormatPreferences {
  books?: string[];
  authors?: string[];
  booksOrSummaries?: string;
  shows?: string[];
  listeningMoments?: string[];
  listeningStyle?: string;
  channels?: string[];
  watchingStyle?: string;
  practiceAreas?: string[];
  practiceCompany?: string;
}

/** Only what was actually answered — a blank key is noise in the prompt. */
export function buildFormatPreferences(a: JourneyAnswers): FormatPreferences {
  const out: FormatPreferences = {};
  const put = <K extends keyof FormatPreferences>(k: K, v: FormatPreferences[K]) => {
    if (Array.isArray(v) ? v.length : v) out[k] = v;
  };
  put('books', a.favouriteBooks);
  put('authors', a.favouriteAuthors);
  put('booksOrSummaries', a.booksOrSummaries);
  put('shows', a.favouriteShows);
  put('listeningMoments', a.listeningMoments);
  put('listeningStyle', a.listeningStyle);
  put('channels', a.favouriteChannels);
  put('watchingStyle', a.watchingStyle);
  put('practiceAreas', a.practiceAreas);
  put('practiceCompany', a.practiceCompany);
  return out;
}

/** Format choices map onto the content types the generator already knows. */
const CONTENT_TYPE_BY_FORMAT: Record<string, string[]> = {
  read: ['article', 'ebook'],
  listen: ['podcast'],
  watch: ['video'],
  do: ['course'],
};

/** And onto the learning style the prompt asks for. */
const STYLE_BY_FORMAT: Record<string, string> = {
  read: 'reading', listen: 'auditory', watch: 'visual', do: 'kinesthetic',
};

export function buildLearningProfile(a: JourneyAnswers): LearningProfilePayload {
  const contentTypes: string[] = [];
  for (const f of a.formats) {
    for (const t of CONTENT_TYPE_BY_FORMAT[f] || []) {
      if (!contentTypes.includes(t)) contentTypes.push(t);
    }
  }
  return {
    learningStyle: STYLE_BY_FORMAT[a.formats[0]] || '',
    preferredContentTypes: contentTypes,
    interests: a.interests,
    // Something somebody wants to practise is a skill gap they have named
    // themselves, which is the most reliable kind. Merged rather than kept
    // apart so the generator weighs them together.
    skillGaps: [...new Set([...a.skillGaps, ...a.practiceAreas])],
    careerGoals: a.careerGoals,
    experienceLevel: a.experienceLevel,
    timeAvailability: a.timeAvailability,
    formatPreferences: buildFormatPreferences(a),
  };
}

/**
 * The questionnaire row, in the shape the existing table and every reader of
 * it already expect. Fields the journey no longer asks about are sent empty
 * rather than dropped, so nothing downstream has to learn a new shape.
 */
export interface StartUpQuestionsPayload {
  interestTopics: string[];
  hobbies: string[];
  relaxationActivities: string[];
  contentPreferences: string[];
  readingPreference: string;
  engagementMethod: string;
  personalityType: string;
  favouriteBooks: string[];
  favouriteAuthors: string[];
  booksOrSummaries: string;
  gender: string;
  ageRange: string;
  dateOfBirth: string;
  relationshipStatus: string;
  lifePrincipleInspirations: string;
  hasChildren: boolean;
  numberOfChildren: number;
  childrenBirthdates: string[];
}

/** Title case for the content preference vocabulary the old form stored. */
const FORMAT_LABEL: Record<string, string> = {
  read: 'Reading', listen: 'Podcast', watch: 'Video', do: 'Interactive',
};

export function buildStartUpQuestions(a: JourneyAnswers): StartUpQuestionsPayload {
  return {
    interestTopics: a.interests,
    hobbies: a.hobbies,
    relaxationActivities: a.recharge,
    contentPreferences: a.formats.map((f) => FORMAT_LABEL[f] || f),
    // The old form asked this as its own question; now it only comes up for
    // somebody who said they read, and "either" is the honest default for
    // everyone else.
    readingPreference: a.booksOrSummaries || (a.formats.includes('read') ? 'Books' : ''),
    engagementMethod: FORMAT_LABEL[a.formats[0]] || '',
    personalityType: a.personalityType,
    favouriteBooks: a.favouriteBooks,
    favouriteAuthors: a.favouriteAuthors,
    booksOrSummaries: a.booksOrSummaries,
    gender: a.gender,
    ageRange: a.ageRange,
    dateOfBirth: a.dateOfBirth,
    relationshipStatus: a.relationshipStatus,
    // Stored as one field by the existing table; joined rather than dropped.
    lifePrincipleInspirations: a.lifePrincipleInspirations.join(', '),
    hasChildren: a.hasChildren,
    // The questionnaire table still stores a count and a list of dates, so
    // they are derived from the rows rather than collected twice.
    numberOfChildren: a.hasChildren ? namedChildren(a).length : 0,
    childrenBirthdates: namedChildren(a).map((c) => c.birthdate).filter(Boolean),
  };
}

/** Children with a name on them; the rest are half-filled rows. */
function namedChildren(a: JourneyAnswers): FamilyMemberInput[] {
  if (!a.hasChildren) return [];
  return a.children
    .map((c) => ({
      name: String(c.name || '').trim(),
      relationship: CHILD_RELATIONSHIP,
      birthdate: String(c.birthdate || '').trim(),
    }))
    .filter((c) => c.name);
}

/** Family members go to their own table, so they travel separately. */
export function buildFamilyMembers(a: JourneyAnswers): FamilyMemberInput[] {
  const others = a.familyMembers
    .map((m) => ({
      name: String(m.name || '').trim(),
      relationship: String(m.relationship || '').trim(),
      birthdate: String(m.birthdate || '').trim(),
    }))
    .filter((m) => m.name && m.relationship);
  // Children travel in the same list; the beat only asks for them apart.
  return [...namedChildren(a), ...others];
}
